/**
 * React Query Hook for HomeHub (Featured Rentals)
 *
 * Provides a hook for reading SSR-hydrated featured listing data.
 * Data can be prefetched by App Router server components and read from the dehydrated cache.
 */

import { useQuery } from '@zg-rentals/react-query5';
import type { SummaryListingDetails } from '@/src/types/summaryListingDetails.type';

// ============================================================================
// Types
// ============================================================================

export type HomeHubCollectionData = {
  title: string;
  collectionType: string;
  resourceId?: string;
  listings: Array<SummaryListingDetails>;
};

// ============================================================================
// Query Keys
// ============================================================================

export const homeHubKeys = {
  all: ['homeHub'] as const,
  collections: () => [...homeHubKeys.all, 'collections'] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Read homeHub collections from the hydrated React Query cache.
 * Data is prefetched during SSR — this hook never triggers a client-side fetch.
 */
export const useHomeHubCollections = () => {
  return useQuery<Array<HomeHubCollectionData>>({
    queryKey: homeHubKeys.collections(),
    queryFn: () => [],
    enabled: false,
    staleTime: Infinity,
  });
};
